import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, finalize, Subscription, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { OverloadMemoService } from 'src/app/services/api/overload-memo/overload-memo.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import {
  OverloadMemoDepartmentGroupSnapshot,
  OverloadMemoListParams,
  OverloadMemoModel,
  OverloadMemoStatus,
} from 'src/app/services/models/overload-memo/overload-memo.model';
import {
  OverloadMemoStatusDisplay,
  getOverloadMemoStatusDisplay,
} from 'src/app/services/models/overload-memo/overload-memo-status-display';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';
import { currentAcademicTerm } from 'src/utils/academic-term-options';
import { RoleID } from 'src/utils/enum/role.enum';

type ReviewTab = 'all' | 'pending' | 'revision' | 'certified';

// ด่านรับรองของ doc_1 ที่ยังค้าง เรียงตามลำดับสายอนุมัติ — ตรงกับ
// OverloadMemoPolicy::CERTIFY_STAGES ฝั่ง API
const CERTIFIABLE_STATUS_CODES: OverloadMemoStatus[] = [
  'pending_education_head',
  'pending_vice_dean',
];

// role ที่ "ลงนามรับรอง" ได้จริง — แอดมินเข้าหน้านี้ได้เพื่อติดตามสถานะเท่านั้น
const CERTIFYING_ROLE_IDS: RoleID[] = [
  RoleID.HEAD_OF_EDUCATION,
  RoleID.VICE_DEAN_ACADEMIC,
];

interface ReviewRow {
  id: number;
  documentNumber: string;
  academicTerm: string;
  year: string;
  effectiveDate: string;
  entriesCount: number;
  statusCode?: OverloadMemoStatus | null;
  statusLabel: string;
  raw: OverloadMemoModel;
}

interface ReviewDataSource {
  data: ReviewRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

@Component({
  selector: 'app-overload-memo-review',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    SharedModule,
  ],
  templateUrl: './overload-memo-review.component.html',
  styleUrl: './overload-memo-review.component.scss',
})
export class OverloadMemoReviewComponent implements OnInit, OnDestroy {
  @ViewChild('statusTemplate', { static: true })
  statusTemplate!: TemplateRef<any>;
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<any>;
  @ViewChild('certifyConfirmDialog')
  certifyConfirmDialog!: ConfirmDialogComponent;
  @ViewChild('actionSuccessDialog')
  actionSuccessDialog?: ConfirmDialogComponent;

  filterForm = inject(FormBuilder).group({
    academicYear: [null as string | null],
    keywords: [''],
  });
  rejectForm = inject(FormBuilder).group({
    comment: ['', Validators.required],
  });

  columns: any[] = [];
  datasource = signal<ReviewDataSource>(this.createDataSource([]));
  tableLoading = signal(false);
  actionLoading = signal(false);
  selectedRow = signal<ReviewRow | null>(null);
  detailOpen = signal(false);
  rejectOpen = signal(false);
  activeTab = signal<ReviewTab>('pending');
  pdfLoading = signal(false);
  pdfError = signal(false);
  pdfPreviewUrl = signal<SafeResourceUrl | null>(null);
  successTitle = signal('');
  successMessage = signal('');
  pageSizeOptions = [10, 20, 50];
  academicYearOptions: string[] = [];

  private rawPdfUrl: string | null = null;
  private currentPage = 1;
  private currentLimit = 50;
  private currentSortParams: DatatableSortParams = { sort: null, direction: null };
  private readonly subscriptions = new Subscription();
  private readonly sortFieldMap: DatatableSortFieldMap = {
    documentNumber: 'document_number',
    academicTerm: 'academic_term_name',
    year: 'academic_term_year',
    effectiveDate: 'effective_date',
    status: 'status',
  };
  private readonly authService = inject(AuthService);
  private readonly overloadMemoService = inject(OverloadMemoService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly studyPeriodService = inject(StudyPeriodService);
  private readonly toast = inject(ToastService);

  ngOnInit(): void {
    this.initColumns();
    this.listenFilters();
    this.loadFilterOptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.revokePdfUrl();
  }

  onTabChange(tab: ReviewTab): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.loadMemos(1);
  }

  onSearch(): void {
    this.loadMemos(1);
  }

  onView(row: ReviewRow): void {
    this.selectedRow.set(row);
    this.detailOpen.set(true);
    this.loadPdfPreview(row.id);
  }

  onCloseDetail(): void {
    if (this.actionLoading()) {
      return;
    }

    this.closeDetail();
  }

  onOpenCertifyConfirm(): void {
    if (!this.canCertify(this.selectedRow())) {
      return;
    }

    this.certifyConfirmDialog.open();
  }

  onConfirmCertify(): void {
    const row = this.selectedRow();
    if (row == null || !this.canCertify(row)) {
      return;
    }

    this.actionLoading.set(true);
    this.overloadMemoService.certifyOverloadMemo(row.id)
      .pipe(finalize(() => this.actionLoading.set(false)))
      .subscribe({
        next: () => {
          this.certifyConfirmDialog.close();
          this.closeDetail();
          this.loadMemos(this.currentPage);
          this.showSuccess('รับรองบันทึกขออนุมัติตารางสอนสำเร็จ', 'รับรองบันทึกขออนุมัติตารางสอนเรียบร้อยแล้ว');
        },
        error: (err) => {
          console.error(err);
          this.toast.toastError(this.extractErrorMessage(err));
        },
      });
  }

  onOpenReject(): void {
    if (!this.canReject(this.selectedRow())) {
      return;
    }

    this.rejectForm.reset({ comment: '' });
    this.rejectOpen.set(true);
  }

  onCloseReject(): void {
    if (this.actionLoading()) {
      return;
    }

    this.rejectOpen.set(false);
  }

  onSubmitReject(): void {
    const row = this.selectedRow();
    if (row == null || !this.canReject(row)) {
      return;
    }

    if (this.rejectForm.invalid) {
      this.rejectForm.markAllAsTouched();
      return;
    }

    this.actionLoading.set(true);
    this.overloadMemoService.rejectOverloadMemo(
      row.id,
      String(this.rejectForm.getRawValue().comment ?? '').trim(),
    ).pipe(
      finalize(() => this.actionLoading.set(false)),
    ).subscribe({
      next: () => {
        this.rejectOpen.set(false);
        this.closeDetail();
        this.loadMemos(this.currentPage);
        this.showSuccess('ส่งกลับแก้ไขสำเร็จ', 'ส่งกลับแก้ไขบันทึกขออนุมัติตารางสอนเรียบร้อยแล้ว');
      },
      error: (err) => {
        console.error(err);
        this.toast.toastError(this.extractErrorMessage(err));
      },
    });
  }

  onDownloadPdf(): void {
    const row = this.selectedRow();
    if (row == null) {
      return;
    }

    this.overloadMemoService.downloadOverloadMemo(row.id)
      .pipe(take(1))
      .subscribe((blob) => this.downloadBlob(blob, `overload_memo_${row.id}.pdf`));
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.loadMemos(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadMemos(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadMemos(1);
  }

  canCertify(row: ReviewRow | null): boolean {
    if (row == null) {
      return false;
    }

    const activeRoleId = this.authService.getActiveRoleId();
    return (
      activeRoleId === RoleID.HEAD_OF_EDUCATION && row.statusCode === 'pending_education_head'
    ) || (
      activeRoleId === RoleID.VICE_DEAN_ACADEMIC && row.statusCode === 'pending_vice_dean'
    );
  }

  canReject(row: ReviewRow | null): boolean {
    return this.canCertify(row);
  }

  // แอดมินอ่านอย่างเดียวบนหน้านี้ — ซ่อนปุ่มรับรอง/ส่งกลับแก้ไขไปเลยแทนที่จะโชว์เป็นปุ่มตาย
  // (แนวเดียวกับปุ่มดาวน์โหลด PDF ที่ใช้ @if ไม่ใช่ [disabled])
  canReviewMemo(): boolean {
    const activeRoleId = this.authService.getActiveRoleId();

    return activeRoleId != null && CERTIFYING_ROLE_IDS.includes(activeRoleId);
  }

  canDownloadPdf(row: ReviewRow | null): boolean {
    return row?.statusCode === 'certified';
  }

  statusDisplay(status?: OverloadMemoStatus | null): OverloadMemoStatusDisplay {
    return getOverloadMemoStatusDisplay(status);
  }

  private showSuccess(title: string, message: string): void {
    this.successTitle.set(title);
    this.successMessage.set(message);
    this.actionSuccessDialog?.open();
  }

  private closeDetail(): void {
    this.detailOpen.set(false);
    this.selectedRow.set(null);
    this.revokePdfUrl();
    this.pdfPreviewUrl.set(null);
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'academicTerm',
        name: 'เทอมการศึกษา',
        sortable: true,
        width: 100,
        minWidth: 90,
      },
      {
        prop: 'year',
        name: 'ปีการศึกษา',
        sortable: true,
        width: 120,
        minWidth: 110,
      },
      {
        prop: 'documentNumber',
        name: 'เลขที่เอกสาร',
        sortable: true,
        width: 150,
        minWidth: 140,
      },
      {
        prop: 'effectiveDate',
        name: 'วันที่',
        sortable: true,
        width: 130,
        minWidth: 120,
      },
      {
        prop: 'entriesCount',
        name: 'จำนวนอาจารย์',
        sortable: false,
        width: 120,
        minWidth: 120,
      },
      {
        prop: 'status',
        name: 'สถานะ',
        sortable: true,
        cellTemplate: this.statusTemplate,
        width: 150,
        minWidth: 140,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        cellClass: 'text-center overload-review-action-cell',
        headerClass: 'text-center overload-review-action-header',
      },
    ];
  }

  private listenFilters(): void {
    this.subscriptions.add(
      this.filterForm.valueChanges
        .pipe(
          debounceTime(250),
          distinctUntilChanged(),
        )
        .subscribe(() => this.loadMemos(1)),
    );
  }

  private loadFilterOptions(): void {
    this.studyPeriodService.getAcademicTermYears().pipe(take(1)).subscribe({
      next: (terms) => {
        this.academicYearOptions = this.buildAcademicYearOptions(terms);
        const sessionTerm = currentAcademicTerm(terms, this.authService.currentUser()?.academic_term);
        const defaultYear = sessionTerm != null ? String(this.toBuddhistYear(sessionTerm.year)) : null;
        this.filterForm.patchValue(
          { academicYear: defaultYear ?? this.academicYearOptions[0] ?? null },
          { emitEvent: false },
        );
        this.loadMemos(1);
      },
      error: (err) => {
        console.error(err);
        this.loadMemos(1);
      },
    });
  }

  private loadMemos(page = this.currentPage): void {
    this.currentPage = page;
    this.tableLoading.set(true);
    this.overloadMemoService.getOverloadMemos({
      ...this.buildFilterParams(),
      page,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => {
        this.datasource.set(this.createDataSource(
          response.data.map((memo) => this.mapMemoToRow(memo)),
          response.page,
          response.limit,
          response.total,
          response.total_pages,
        ));
      },
      error: (err) => {
        console.error(err);
        this.datasource.set(this.createDataSource([], page, this.currentLimit));
      },
    });
  }

  private buildFilterParams(): OverloadMemoListParams {
    const { academicYear, keywords } = this.filterForm.getRawValue();
    const params: OverloadMemoListParams = {};

    if (academicYear != null && academicYear !== '') {
      params.academic_year = academicYear;
    }

    const normalizedKeywords = String(keywords ?? '').trim();
    if (normalizedKeywords !== '') {
      params.keywords = normalizedKeywords;
    }

    const status = this.statusForActiveTab();
    if (status != null) {
      params.status = status;
    }

    return params;
  }

  private statusForActiveTab(): string | null {
    if (this.activeTab() === 'certified') {
      return 'certified';
    }

    if (this.activeTab() === 'revision') {
      return 'revision_requested';
    }

    if (this.activeTab() !== 'pending') {
      return null;
    }

    const activeRoleId = this.authService.getActiveRoleId();
    if (activeRoleId === RoleID.HEAD_OF_EDUCATION) {
      return 'pending_education_head';
    }

    if (activeRoleId === RoleID.VICE_DEAN_ACADEMIC) {
      return 'pending_vice_dean';
    }

    if (activeRoleId === RoleID.ADMIN) {
      return CERTIFIABLE_STATUS_CODES.join(',');
    }

    return null;
  }

  private mapMemoToRow(memo: OverloadMemoModel): ReviewRow {
    return {
      id: memo.id,
      documentNumber: memo.document_number,
      academicTerm: memo.academic_term?.name ?? '-',
      year: memo.academic_term?.year != null ? String(this.toBuddhistYear(memo.academic_term.year)) : memo.academic_year,
      effectiveDate: this.formatDate(memo.effective_date),
      entriesCount: memo.entries_count ?? this.entriesCountFromGroups(memo.entries_snapshot),
      statusCode: memo.status_code,
      statusLabel: memo.status_label ?? '-',
      raw: memo,
    };
  }

  private createDataSource(
    rows: ReviewRow[],
    page = 1,
    limit = 50,
    total = rows.length,
    totalPage = Math.max(Math.ceil(total / limit), 1),
  ): ReviewDataSource {
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      limit,
      offset: (normalizedPage - 1) * limit,
      page: normalizedPage,
      total,
      total_page: totalPage,
    };
  }

  private loadPdfPreview(id: number): void {
    this.pdfLoading.set(true);
    this.pdfError.set(false);
    this.overloadMemoService.downloadOverloadMemo(id)
      .pipe(finalize(() => this.pdfLoading.set(false)))
      .subscribe({
        next: (blob) => {
          this.revokePdfUrl();
          this.rawPdfUrl = window.URL.createObjectURL(blob);
          // Hide the native PDF viewer chrome (toolbar + side panes) so the
          // preview is read-only — download is gated behind certification.
          this.pdfPreviewUrl.set(
            this.sanitizer.bypassSecurityTrustResourceUrl(`${this.rawPdfUrl}#toolbar=0&navpanes=0`),
          );
        },
        error: (err) => {
          console.error(err);
          this.revokePdfUrl();
          this.pdfPreviewUrl.set(null);
          this.pdfError.set(true);
        },
      });
  }

  private revokePdfUrl(): void {
    if (this.rawPdfUrl != null) {
      window.URL.revokeObjectURL(this.rawPdfUrl);
      this.rawPdfUrl = null;
    }
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private formatDate(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return [
      String(date.getDate()).padStart(2, '0'),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(this.toBuddhistYear(date.getFullYear())),
    ].join('/');
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(
      new Set(
        terms
          .map((term) => this.toBuddhistYear(term.year))
          .filter((year) => Number.isFinite(year))
          .map((year) => String(year)),
      ),
    ).sort((a, b) => Number(b) - Number(a));
  }

  private entriesCountFromGroups(groups: OverloadMemoDepartmentGroupSnapshot[]): number {
    return groups.reduce((total, group) => total + group.teachers.length, 0);
  }

  private extractErrorMessage(err: unknown): string {
    if (err !== null && typeof err === 'object' && 'error' in err) {
      const error = (err as { error?: unknown }).error;
      if (error !== null && typeof error === 'object' && 'errors' in error) {
        const errors = (error as { errors?: unknown }).errors;
        if (Array.isArray(errors) && errors.length > 0) {
          return String(errors[0]);
        }
        if (typeof errors === 'string') {
          return errors;
        }
      }
    }

    return 'ไม่สามารถดำเนินการได้';
  }
}
